import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import {
  CheckCircle2, XCircle, Loader2, CreditCard,
  Calendar, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';

const WALLET_BREADCRUMBS = [{ label: 'Абонамент', href: '/wallet' }];
const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const { subscription_price_eur: price } = useAppSettings();

  const [subStatus, setSubStatus] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  const subSessionId = new URLSearchParams(location.search).get('sub_session_id');

  useEffect(() => {
    loadData();
    if (subSessionId) {
      setPaymentStatus('checking');
      setPolling(true);
      pollSubStatus(subSessionId, 0);
    }
  }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      const [statusRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/subscription/payments`),
      ]);
      setSubStatus(statusRes.data.status);
      setExpiresAt(statusRes.data.expires_at);
      setPayments(paymentsRes.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const pollSubStatus = async (sid, attempt) => {
    if (attempt >= 10) { setPaymentStatus('failed'); setPolling(false); return; }
    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sid}`);
      if (res.data.subscription_status === 'active') {
        setPaymentStatus('paid');
        setPolling(false);
        await refreshUser();
        await loadData();
        toast.success('Абонаментът е активиран успешно!');
      } else if (res.data.status === 'expired') {
        setPaymentStatus('failed');
        setPolling(false);
      } else {
        setTimeout(() => pollSubStatus(sid, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollSubStatus(sid, attempt + 1), 2000);
    }
  };

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        plan_id: '',
        success_path: '/wallet',
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при създаване на абонамент');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isActive = subStatus === 'active';
  const expiryDate = expiresAt
    ? new Date(expiresAt).toLocaleDateString('bg-BG', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const daysRemaining = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <>
      <SEOMeta
        title="Абонамент"
        description="Управлявай абонамента си в portion.bg."
        canonical="/wallet"
        noindex={true}
        breadcrumbs={WALLET_BREADCRUMBS}
      />
      <PageBreadcrumb items={WALLET_BREADCRUMBS} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Абонамент</h1>
            <p className="text-slate-500 dark:text-slate-400">Управлявай достъпа до пълния хранителен план</p>
          </div>

          {/* Status banners */}
          {paymentStatus === 'checking' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 flex items-center gap-3 mb-6">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">Проверявам статуса на плащането...</p>
            </div>
          )}
          {paymentStatus === 'paid' && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40 rounded-xl p-4 flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <p className="text-sm text-emerald-800 dark:text-emerald-300 font-medium">Абонаментът е активиран! Вече имаш пълен достъп до всичките 7 дни.</p>
            </div>
          )}
          {paymentStatus === 'failed' && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl p-4 flex items-center gap-3 mb-6">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-300">Плащането не е успешно или е изтекло. Моля опитайте отново.</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* ── Subscription Status Card ── */}
            <div className="lg:col-span-4">
              <Card className="rounded-xl overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}>
                <div className="bg-gray-900 px-6 pt-7 pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-white/40">Статус</p>
                    <span
                      data-testid="subscription-status-badge"
                      className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                        isActive
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {isActive ? 'Активен' : 'Неактивен'}
                    </span>
                  </div>
                  {isActive ? (
                    <div>
                      <p className="text-4xl font-bold text-white leading-none mb-1 tabular-nums">{daysRemaining}</p>
                      <p className="text-sm text-white/40">оставащи дни</p>
                      {expiryDate && (
                        <div className="flex items-center gap-1.5 mt-3">
                          <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                          <p className="text-xs text-white/60">Изтича: {expiryDate}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-white/60 leading-relaxed">
                      Абонирай се за пълен достъп до всичките 7 дни от плана.
                    </p>
                  )}
                </div>

                <CardContent className="p-6">
                  {!isActive ? (
                    <>
                      <div className="mb-5 space-y-2">
                        {['7-дневен персонализиран план', 'Списък за пазаруване', 'PDF изтегляне', 'Неограничени нови планове'].map(t => (
                          <div key={t} className="flex items-center gap-2.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <span className="text-sm text-slate-600 dark:text-slate-300">{t}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleSubscribe}
                        disabled={checkoutLoading || polling}
                        data-testid="wallet-subscribe-button"
                        className="w-full bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl py-3 px-4 flex items-center justify-between transition-colors duration-200"
                      >
                        {checkoutLoading ? (
                          <div className="flex items-center gap-2 mx-auto">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm font-medium">Обработва се...</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 opacity-90" />
                              <span className="text-sm font-semibold">Абонирай се сега</span>
                            </div>
                            <span className="text-sm font-bold">{Number(price).toFixed(2).replace('.', ',')} €/мес.</span>
                          </>
                        )}
                      </button>
                      <p className="text-xs text-center text-slate-400 dark:text-slate-500 mt-2">Сигурно плащане чрез Stripe</p>
                    </>
                  ) : (
                    <div className="text-center py-2">
                      <CheckCircle2 className="w-9 h-9 text-emerald-500 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Абонаментът е активен</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">Имаш пълен достъп до всички функции</p>
                      <Link
                        to="/my-plan"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                      >
                        Виж плана <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Payment History ── */}
            <div className="lg:col-span-8">
              <Card className="rounded-xl" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    История на плащанията
                    {payments.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
                        ({payments.length} {payments.length === 1 ? 'запис' : 'записа'})
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0" data-testid="subscription-payment-history">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
                    </div>
                  ) : payments.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                      <p className="text-sm text-slate-400 dark:text-slate-500">Няма плащания още</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-50 dark:divide-slate-700">
                      {payments.map(p => (
                        <div key={p.id} data-testid="payment-row" className="flex items-center justify-between px-6 py-4 hover:bg-slate-50/70 dark:hover:bg-slate-700/50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              p.payment_status === 'paid'
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                            }`}>
                              {p.payment_status === 'paid' ? 'Платено' : 'Изчакване'}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Месечен абонамент — portion.bg</p>
                              <p className="text-xs text-slate-400 dark:text-slate-500">{new Date(p.created_at).toLocaleString('bg-BG')}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{Number(p.amount).toFixed(2)} €</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

        </motion.div>
      </div>
    </>
  );
}
